import React, { useEffect, useState, useCallback } from 'react';
import { X, Calendar as CalendarIcon, Moon, Sun, Bell, BellOff, Clock, ChevronRight, ChevronLeft, Settings, Check, AlertCircle, Share2 } from 'lucide-react';
import { EkadashiDate, EkadashiReminderSettings, ReminderType, EKADASHI_DATES } from '../types';
import { Calendar } from './ui/calendar';
import { ShareModal } from './ShareModal';
import { 
  shouldShowEkadashiReminder, 
  markEkadashiReminderShown, 
  getDaysUntilEkadashi,
  getUpcomingEkadashis,
  getEkadashiSettings,
  saveEkadashiSettings
} from '../services/mockService';

interface Props {
  onClose?: () => void;
  forceShow?: boolean;
}

// Helper to get the VAISHNAV date for an Ekadashi (prioritizes vaishnavDate over base date)
// Helper: Parse "YYYY-MM-DD" as LOCAL date (not UTC)
const parseLocalDate = (dateStr: string): Date => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const getVaishnavDateStr = (ekadashi: EkadashiDate): string => {
  return ekadashi.vaishnavDate || ekadashi.date;
};

// Helper to check if a date is an Ekadashi (checks against VAISHNAV dates)
const getEkadashiForDate = (date: Date): EkadashiDate | null => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const dateStr = `${y}-${m}-${d}`;
  // Check VAISHNAV date first (priority), then fall back to base date
  return EKADASHI_DATES.find(e => getVaishnavDateStr(e) === dateStr) || null;
};

// Helper to get all VAISHNAV Ekadashi dates for the calendar highlighting
const getEkadashiDates = (): Date[] => {
  return EKADASHI_DATES.map(e => parseLocalDate(getVaishnavDateStr(e)));
};

// Detect Capacitor native
const isNativeApp = (() => {
  try {
    const { Capacitor } = require('@capacitor/core');
    return Capacitor.isNativePlatform();
  } catch { return false; }
})();

// Notification helpers
const requestNotificationPermission = async (): Promise<boolean> => {
  if (isNativeApp) {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      const result = await PushNotifications.requestPermissions();
      if (result.receive === 'granted') {
        await PushNotifications.register();
        return true;
      }
    } catch (e) { console.error('Native push error:', e); }
    return false;
  }

  if (!('Notification' in window)) {
    return false;
  }
  
  if (Notification.permission === 'granted') {
    return true;
  }
  
  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }
  
  return false;
};

const scheduleNotification = (title: string, body: string, scheduledTime: Date) => {
  const now = new Date();
  const delay = scheduledTime.getTime() - now.getTime();
  
  if (delay > 0) {
    setTimeout(() => {
      if (isNativeApp) {
        // In Capacitor, use vibrate + alert
        if ('vibrate' in navigator) navigator.vibrate([200, 100, 200]);
        alert(`${title}\n\n${body}`);
      } else if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(title, {
          body,
          icon: '/favicon.ico',
          tag: 'ekadashi-reminder'
        });
      }
    }, delay);
  }
};

export const EkadashiReminder: React.FC<Props> = ({ onClose, forceShow = false }) => {
  const [isVisible, setIsVisible] = useState(forceShow);
  const [currentEkadashi, setCurrentEkadashi] = useState<EkadashiDate | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showCalendar, setShowCalendar] = useState(forceShow);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedEkadashi, setSelectedEkadashi] = useState<EkadashiDate | null>(null);
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [upcomingEkadashis, setUpcomingEkadashis] = useState<EkadashiDate[]>([]);
  const [settings, setSettings] = useState<EkadashiReminderSettings>(getEkadashiSettings());
  const [notificationStatus, setNotificationStatus] = useState<'granted' | 'denied' | 'default'>('default');
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    const upcoming = getUpcomingEkadashis(12);
    setUpcomingEkadashis(upcoming);
    
    // Check notification permission status
    if (isNativeApp) {
      setNotificationStatus(localStorage.getItem('fcm_token') ? 'granted' : 'default');
    } else if ('Notification' in window) {
      setNotificationStatus(Notification.permission);
    }
    
    if (forceShow) {
      if (upcoming.length > 0) {
        setCurrentEkadashi(upcoming[0]);
      }
      setIsVisible(true);
    } else {
      const { show, ekadashi } = shouldShowEkadashiReminder();
      if (show && ekadashi) {
        setCurrentEkadashi(ekadashi);
        setIsVisible(true);
        markEkadashiReminderShown(ekadashi);
      }
    }
  }, [forceShow]);

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  const handleSaveSettings = async () => {
    // If notifications are being enabled, request permission
    if (settings.notificationsEnabled && notificationStatus !== 'granted') {
      const granted = await requestNotificationPermission();
      if (granted) {
        setNotificationStatus('granted');
        // Schedule notifications for upcoming Ekadashis
        scheduleUpcomingNotifications();
      } else {
        setNotificationStatus('denied');
        setSettings({ ...settings, notificationsEnabled: false });
      }
    }
    
    saveEkadashiSettings(settings);
    setShowSettings(false);
  };

  const scheduleUpcomingNotifications = useCallback(() => {
    if (!settings.notificationsEnabled || notificationStatus !== 'granted') return;
    
    const upcoming = getUpcomingEkadashis(3);
    upcoming.forEach(ekadashi => {
      const ekadashiDate = parseLocalDate(ekadashi.date);
      let notificationDate: Date;
      
      switch (settings.reminderType) {
        case 'day_before':
          notificationDate = new Date(ekadashiDate);
          notificationDate.setDate(notificationDate.getDate() - 1);
          notificationDate.setHours(18, 0, 0, 0); // 6 PM day before
          break;
        case 'morning_of':
          notificationDate = new Date(ekadashiDate);
          const [hours, minutes] = settings.reminderTime.split(':').map(Number);
          notificationDate.setHours(hours, minutes, 0, 0);
          break;
        case 'custom':
        default:
          notificationDate = new Date(ekadashiDate);
          notificationDate.setDate(notificationDate.getDate() - settings.reminderDaysBefore);
          const [h, m] = settings.reminderTime.split(':').map(Number);
          notificationDate.setHours(h, m, 0, 0);
          break;
      }
      
      scheduleNotification(
        `🙏 ${ekadashi.name}`,
        `एकादशी व्रत का पावन अवसर! ${ekadashi.description || ''}`,
        notificationDate
      );
    });
  }, [settings, notificationStatus]);

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date) {
      const ekadashi = getEkadashiForDate(date);
      setSelectedEkadashi(ekadashi);
    } else {
      setSelectedEkadashi(null);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = parseLocalDate(dateStr);
    return date.toLocaleDateString('en-IN', { 
      weekday: 'long',
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const formatShortDate = (dateStr: string) => {
    const date = parseLocalDate(dateStr);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short'
    });
  };

  if (!isVisible) return null;

  const daysUntil = currentEkadashi ? getDaysUntilEkadashi(currentEkadashi) : 0;
  const ekadashiDates = getEkadashiDates();

  // Custom day rendering for calendar to highlight VAISHNAV Ekadashi dates
  const modifiers = {
    ekadashi: ekadashiDates,
    shukla: EKADASHI_DATES.filter(e => e.paksha === 'Shukla').map(e => parseLocalDate(getVaishnavDateStr(e))),
    krishna: EKADASHI_DATES.filter(e => e.paksha === 'Krishna').map(e => parseLocalDate(getVaishnavDateStr(e)))
  };

  const modifiersStyles = {
    ekadashi: {
      backgroundColor: 'rgb(251 191 36 / 0.3)',
      borderRadius: '50%',
      fontWeight: 'bold'
    },
    shukla: {
      border: '2px solid rgb(234 179 8)',
    },
    krishna: {
      border: '2px solid rgb(99 102 241)',
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-stone-900 dark:to-stone-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden border border-amber-200 dark:border-stone-700 flex flex-col">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 dark:from-amber-600 dark:to-orange-600 p-4 text-white relative shrink-0">
          <button 
            onClick={handleClose}
            className="absolute top-3 right-3 p-1.5 hover:bg-white/20 rounded-full transition-colors"
            data-testid="close-ekadashi-modal"
          >
            <X size={20} />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl">
              {currentEkadashi?.paksha === 'Shukla' ? <Sun size={28} /> : <Moon size={28} />}
            </div>
            <div>
              <h2 className="font-bold text-lg">एकादशी कैलेंडर</h2>
              <p className="text-amber-100 text-xs">Ekadashi Calendar & Reminders</p>
            </div>
          </div>
        </div>

        {/* Main Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          {/* Default View - Next Ekadashi Info */}
          {!showSettings && !showCalendar && currentEkadashi && (
            <div className="p-5 space-y-4">
              {/* Countdown Badge */}
              <div className="text-center">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${
                  daysUntil === 0 
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' 
                    : daysUntil === 1 
                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                      : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                }`}>
                  <CalendarIcon size={16} />
                  {daysUntil === 0 ? 'आज है एकादशी!' : daysUntil === 1 ? 'कल है एकादशी!' : `${daysUntil} दिन बाकी`}
                </div>
              </div>

              {/* Ekadashi Details Card */}
              <div className="bg-white dark:bg-stone-800 rounded-xl p-4 border border-amber-100 dark:border-stone-700 shadow-sm">
                <h3 className="font-bold text-lg text-amber-700 dark:text-amber-400 mb-1">
                  {currentEkadashi.name}
                </h3>
                <p className="text-sm text-gray-600 dark:text-stone-400 mb-3">
                  {formatDate(getVaishnavDateStr(currentEkadashi))}
                </p>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                    currentEkadashi.paksha === 'Shukla' 
                      ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' 
                      : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
                  }`}>
                    {currentEkadashi.paksha === 'Shukla' ? '🌕 शुक्ल पक्ष' : '🌑 कृष्ण पक्ष'}
                  </span>
                  <span className="px-2 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                    {currentEkadashi.month} मास
                  </span>
                </div>

                {currentEkadashi.description && (
                  <p className="text-sm text-gray-600 dark:text-stone-400 italic mb-3">
                    "{currentEkadashi.description}"
                  </p>
                )}

                {/* Tithi Timings */}
                {(currentEkadashi.smpikaStart || currentEkadashi.smpikaEnd) && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 mb-3">
                    <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 font-bold text-sm mb-2">
                      <Clock size={14} />
                      <span>एकादशी तिथि समय</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-gray-500 dark:text-stone-400">आरंभ: </span>
                        <span className="font-medium text-gray-700 dark:text-stone-200">{currentEkadashi.smpikaStart}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-stone-400">समाप्त: </span>
                        <span className="font-medium text-gray-700 dark:text-stone-200">{currentEkadashi.smpikaEnd}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Vaishnav Paran Timings */}
                <div className="space-y-2">
                  {/* Vaishnav Paran */}
                  {(currentEkadashi.vaishnavParanStart || currentEkadashi.vaishnavParanEnd) && (
                    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 border border-orange-100 dark:border-orange-800">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-orange-700 dark:text-orange-400 font-bold text-sm">
                          <span className="text-base">🙏</span>
                          <span>वैष्णव एकादशी</span>
                        </div>
                        {currentEkadashi.vaishnavDate && (
                          <span className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                            {parseLocalDate(currentEkadashi.vaishnavDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <Sun size={12} className="text-orange-600 dark:text-orange-400" />
                        <span className="text-gray-500 dark:text-stone-400">पारण: </span>
                        <span className="font-medium text-gray-700 dark:text-stone-200">
                          {currentEkadashi.vaishnavParanStart} - {currentEkadashi.vaishnavParanEnd}
                        </span>
                        <span className="text-[10px] text-orange-500">({currentEkadashi.vaishnavParanNote})</span>
                      </div>
                    </div>
                  )}

                  {/* Vaishnav only */}
                </div>
              </div>

              {/* Guidelines */}
              <div className="bg-amber-50 dark:bg-stone-800/50 rounded-xl p-4 border border-amber-100 dark:border-stone-700">
                <h4 className="font-bold text-sm text-amber-700 dark:text-amber-400 mb-2">व्रत नियम:</h4>
                <ul className="text-xs text-gray-600 dark:text-stone-400 space-y-1">
                  <li>• एकादशी के दिन अन्न (चावल, गेहूं) का त्याग करें</li>
                  <li>• फलाहार या निर्जल व्रत रखें</li>
                  <li>• विष्णु सहस्रनाम या राम नाम जप करें</li>
                  <li>• द्वादशी को पारण (व्रत खोलना) करें</li>
                </ul>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => setShowCalendar(true)}
                  className="flex-1 py-2.5 px-4 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
                  data-testid="view-calendar-btn"
                >
                  <CalendarIcon size={16} /> कैलेंडर
                </button>
                <button
                  onClick={() => setShowShare(true)}
                  data-testid="ekadashi-share-btn"
                  className="py-2.5 px-4 bg-gradient-to-r from-saffron to-orange-500 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:shadow-lg transition-all"
                >
                  <Share2 size={16} /> शेयर
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className="py-2.5 px-4 bg-gray-100 dark:bg-stone-700 text-gray-600 dark:text-stone-300 rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-200 dark:hover:bg-stone-600 transition-colors"
                  data-testid="ekadashi-settings-btn"
                >
                  <Bell size={16} />
                </button>
              </div>

              {/* Dismiss */}
              <button
                onClick={handleClose}
                className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-bold text-sm hover:from-amber-600 hover:to-orange-600 transition-all shadow-md"
                data-testid="dismiss-ekadashi-btn"
              >
                जय श्री राम
              </button>

              {currentEkadashi && (
                <ShareModal
                  isOpen={showShare}
                  onClose={() => setShowShare(false)}
                  content={{
                    title: `${currentEkadashi.name} - एकादशी`,
                    text: `${daysUntil === 0 ? 'आज है' : daysUntil === 1 ? 'कल है' : `${daysUntil} दिन में है`} ${currentEkadashi.name}!\n\n${currentEkadashi.paksha === 'Shukla' ? '🌕 शुक्ल पक्ष' : '🌑 कृष्ण पक्ष'} | ${currentEkadashi.month} मास\n\n${currentEkadashi.description || ''}\n\nव्रत नियम:\n• एकादशी के दिन अन्न का त्याग करें\n• फलाहार या निर्जल व्रत रखें\n• विष्णु सहस्रनाम या राम नाम जप करें\n\nगोलोक दिव्य नाम कोष`,
                    fileName: `ekadashi-${currentEkadashi.date}.png`,
                  }}
                />
              )}
            </div>
          )}

          {/* Calendar View */}
          {showCalendar && (
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-amber-700 dark:text-amber-400">एकादशी कैलेंडर</h3>
                <button
                  onClick={() => {
                    setShowCalendar(false);
                    setSelectedEkadashi(null);
                    setSelectedDate(undefined);
                  }}
                  className="text-xs text-amber-600 dark:text-amber-400 font-bold hover:underline flex items-center gap-1"
                >
                  <ChevronLeft size={14} /> वापस
                </button>
              </div>

              {/* Legend */}
              <div className="flex items-center justify-center gap-4 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 rounded-full border-2 border-yellow-500 bg-amber-100"></div>
                  <span className="text-gray-600 dark:text-stone-400">शुक्ल पक्ष</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 rounded-full border-2 border-indigo-500 bg-amber-100"></div>
                  <span className="text-gray-600 dark:text-stone-400">कृष्ण पक्ष</span>
                </div>
              </div>

              {/* Calendar */}
              <div className="flex justify-center bg-white dark:bg-stone-800 rounded-xl p-2 border border-amber-100 dark:border-stone-700">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  month={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  modifiers={modifiers}
                  modifiersStyles={modifiersStyles}
                  classNames={{}}
                  className="rounded-lg"
                />
              </div>

              {/* Selected Ekadashi Details */}
              {selectedEkadashi && (
                <div className="bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 rounded-xl p-4 border border-amber-200 dark:border-amber-700 animate-fade-in">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <h4 className="font-bold text-amber-700 dark:text-amber-400">
                        {selectedEkadashi.name}
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-stone-400 mt-0.5">
                        {formatDate(getVaishnavDateStr(selectedEkadashi))}
                      </p>
                      <div className="flex gap-1 mt-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          selectedEkadashi.paksha === 'Shukla' 
                            ? 'bg-yellow-200 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400' 
                            : 'bg-indigo-200 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400'
                        }`}>
                          {selectedEkadashi.paksha === 'Shukla' ? '🌕 शुक्ल' : '🌑 कृष्ण'}
                        </span>
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-orange-200 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400">
                          {selectedEkadashi.month}
                        </span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`p-2 rounded-lg ${
                        selectedEkadashi.paksha === 'Shukla' ? 'bg-yellow-200 dark:bg-yellow-900/30' : 'bg-indigo-200 dark:bg-indigo-900/30'
                      }`}>
                        {selectedEkadashi.paksha === 'Shukla' ? <Sun size={20} className="text-yellow-700 dark:text-yellow-400" /> : <Moon size={20} className="text-indigo-700 dark:text-indigo-400" />}
                      </div>
                    </div>
                  </div>
                  
                  {selectedEkadashi.description && (
                    <p className="text-xs text-gray-600 dark:text-stone-400 mt-2 italic">
                      "{selectedEkadashi.description}"
                    </p>
                  )}

                  {/* Tithi Timings */}
                  {(selectedEkadashi.smpikaStart || selectedEkadashi.smpikaEnd) && (
                    <div className="bg-white/50 dark:bg-stone-800/50 rounded-lg p-2 mt-3">
                      <div className="text-[10px] font-bold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                        <Clock size={10} /> एकादशी तिथि समय
                      </div>
                      <div className="text-[10px] text-gray-600 dark:text-stone-400 mt-1 grid grid-cols-2 gap-1">
                        <div>आरंभ: {selectedEkadashi.smpikaStart}</div>
                        <div>समाप्त: {selectedEkadashi.smpikaEnd}</div>
                      </div>
                    </div>
                  )}

                  {/* Vaishnav Paran */}
                  <div className="grid grid-cols-1 gap-2 mt-2">
                    {/* Vaishnav */}
                    {(selectedEkadashi.vaishnavParanStart) && (
                      <div className="bg-orange-50/80 dark:bg-orange-900/20 rounded-lg p-2 border border-orange-100 dark:border-orange-800">
                        <div className="flex items-center justify-between">
                          <div className="text-[10px] font-bold text-orange-600 dark:text-orange-400 flex items-center gap-1">
                            🙏 वैष्णव
                          </div>
                          <span className="text-[9px] text-orange-500">{selectedEkadashi.vaishnavParanNote}</span>
                        </div>
                        <div className="text-[10px] text-gray-600 dark:text-stone-400 mt-1">
                          पारण: {selectedEkadashi.vaishnavParanStart} - {selectedEkadashi.vaishnavParanEnd}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Upcoming List */}
              <div className="space-y-2">
                <h4 className="font-bold text-sm text-gray-700 dark:text-stone-300">आगामी एकादशी</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {upcomingEkadashis.slice(0, 6).map((ekadashi, idx) => {
                    const days = getDaysUntilEkadashi(ekadashi);
                    return (
                      <button 
                        key={getVaishnavDateStr(ekadashi)}
                        onClick={() => {
                          setSelectedDate(parseLocalDate(getVaishnavDateStr(ekadashi)));
                          setSelectedEkadashi(ekadashi);
                          setCalendarMonth(parseLocalDate(getVaishnavDateStr(ekadashi)));
                        }}
                        className={`w-full p-3 rounded-xl border transition-all text-left hover:shadow-md ${
                          idx === 0 
                            ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700' 
                            : 'bg-white dark:bg-stone-800 border-gray-100 dark:border-stone-700 hover:border-amber-300 dark:hover:border-amber-700'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1">
                            <div className="font-bold text-sm text-gray-800 dark:text-stone-200 line-clamp-1">
                              {ekadashi.name}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-stone-400 mt-0.5">
                              {formatDate(getVaishnavDateStr(ekadashi))}
                            </div>
                          </div>
                          <div className={`text-right shrink-0 ${
                            days === 0 ? 'text-red-600 dark:text-red-400' : 
                            days <= 3 ? 'text-amber-600 dark:text-amber-400' : 
                            'text-gray-500 dark:text-stone-400'
                          }`}>
                            <div className="text-lg font-bold">{formatShortDate(getVaishnavDateStr(ekadashi))}</div>
                            <div className="text-[10px]">
                              {days === 0 ? 'आज' : days === 1 ? 'कल' : `${days} दिन`}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="text-center text-xs text-gray-400 dark:text-stone-500 pt-2">
                Data based on Drik Panchang (India)
              </div>

              <button
                onClick={handleClose}
                className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-bold text-sm hover:from-amber-600 hover:to-orange-600 transition-all"
              >
                बंद करें
              </button>
            </div>
          )}

          {/* Settings View */}
          {showSettings && (
            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-amber-700 dark:text-amber-400">रिमाइंडर सेटिंग्स</h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-xs text-amber-600 dark:text-amber-400 font-bold hover:underline flex items-center gap-1"
                >
                  <ChevronLeft size={14} /> वापस
                </button>
              </div>

              <div className="space-y-4">
                {/* Enable/Disable App Reminder */}
                <div className="flex items-center justify-between p-3 bg-white dark:bg-stone-800 rounded-xl border border-gray-100 dark:border-stone-700">
                  <div className="flex items-center gap-3">
                    <Bell size={20} className="text-amber-500" />
                    <div>
                      <div className="font-bold text-sm text-gray-800 dark:text-stone-200">ऐप रिमाइंडर</div>
                      <div className="text-xs text-gray-500 dark:text-stone-400">In-app popup reminder</div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      settings.enabled ? 'bg-amber-500' : 'bg-gray-300 dark:bg-stone-600'
                    }`}
                    data-testid="toggle-ekadashi-reminder"
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      settings.enabled ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>

                {/* Browser Notifications */}
                <div className="p-3 bg-white dark:bg-stone-800 rounded-xl border border-gray-100 dark:border-stone-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {notificationStatus === 'granted' ? (
                        <Bell size={20} className="text-green-500" />
                      ) : notificationStatus === 'denied' ? (
                        <BellOff size={20} className="text-red-500" />
                      ) : (
                        <Bell size={20} className="text-gray-400" />
                      )}
                      <div>
                        <div className="font-bold text-sm text-gray-800 dark:text-stone-200">ब्राउज़र नोटिफिकेशन</div>
                        <div className="text-xs text-gray-500 dark:text-stone-400">
                          {notificationStatus === 'granted' ? 'Enabled' : notificationStatus === 'denied' ? 'Blocked by browser' : 'Push notifications'}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setSettings({ ...settings, notificationsEnabled: !settings.notificationsEnabled })}
                      disabled={notificationStatus === 'denied'}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        settings.notificationsEnabled && notificationStatus !== 'denied' 
                          ? 'bg-green-500' 
                          : 'bg-gray-300 dark:bg-stone-600'
                      } ${notificationStatus === 'denied' ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid="toggle-browser-notifications"
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        settings.notificationsEnabled && notificationStatus !== 'denied' ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                  
                  {notificationStatus === 'denied' && (
                    <div className="mt-2 flex items-start gap-2 text-xs text-red-600 dark:text-red-400">
                      <AlertCircle size={14} className="shrink-0 mt-0.5" />
                      <span>ब्राउज़र में नोटिफिकेशन ब्लॉक है। कृपया ब्राउज़र सेटिंग्स में जाकर इसे अनब्लॉक करें।</span>
                    </div>
                  )}
                </div>

                {/* Reminder Type Selection */}
                <div className="p-3 bg-white dark:bg-stone-800 rounded-xl border border-gray-100 dark:border-stone-700">
                  <div className="flex items-center gap-3 mb-3">
                    <Settings size={20} className="text-amber-500" />
                    <div>
                      <div className="font-bold text-sm text-gray-800 dark:text-stone-200">रिमाइंडर का समय</div>
                      <div className="text-xs text-gray-500 dark:text-stone-400">When to remind you</div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {/* Option 1: Day Before */}
                    <button
                      onClick={() => setSettings({ ...settings, reminderType: 'day_before', reminderDaysBefore: 1 })}
                      className={`w-full p-3 rounded-lg border-2 transition-all flex items-center justify-between ${
                        settings.reminderType === 'day_before'
                          ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                          : 'border-gray-200 dark:border-stone-600 hover:border-amber-300'
                      }`}
                    >
                      <div className="text-left">
                        <div className="font-bold text-sm text-gray-800 dark:text-stone-200">एक दिन पहले</div>
                        <div className="text-xs text-gray-500 dark:text-stone-400">Evening before Ekadashi (6 PM)</div>
                      </div>
                      {settings.reminderType === 'day_before' && (
                        <Check size={18} className="text-amber-500" />
                      )}
                    </button>

                    {/* Option 2: Morning Of */}
                    <button
                      onClick={() => setSettings({ ...settings, reminderType: 'morning_of', reminderDaysBefore: 0 })}
                      className={`w-full p-3 rounded-lg border-2 transition-all flex items-center justify-between ${
                        settings.reminderType === 'morning_of'
                          ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                          : 'border-gray-200 dark:border-stone-600 hover:border-amber-300'
                      }`}
                    >
                      <div className="text-left">
                        <div className="font-bold text-sm text-gray-800 dark:text-stone-200">एकादशी की सुबह</div>
                        <div className="text-xs text-gray-500 dark:text-stone-400">Morning of Ekadashi</div>
                      </div>
                      {settings.reminderType === 'morning_of' && (
                        <Check size={18} className="text-amber-500" />
                      )}
                    </button>

                    {/* Option 3: Custom */}
                    <button
                      onClick={() => setSettings({ ...settings, reminderType: 'custom' })}
                      className={`w-full p-3 rounded-lg border-2 transition-all flex items-center justify-between ${
                        settings.reminderType === 'custom'
                          ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                          : 'border-gray-200 dark:border-stone-600 hover:border-amber-300'
                      }`}
                    >
                      <div className="text-left">
                        <div className="font-bold text-sm text-gray-800 dark:text-stone-200">कस्टम समय</div>
                        <div className="text-xs text-gray-500 dark:text-stone-400">Set your own reminder time</div>
                      </div>
                      {settings.reminderType === 'custom' && (
                        <Check size={18} className="text-amber-500" />
                      )}
                    </button>
                  </div>

                  {/* Custom Time Options */}
                  {settings.reminderType === 'custom' && (
                    <div className="mt-4 space-y-3 pt-3 border-t border-gray-200 dark:border-stone-600">
                      {/* Days Before */}
                      <div>
                        <label className="text-xs font-bold text-gray-600 dark:text-stone-400 mb-2 block">
                          कितने दिन पहले?
                        </label>
                        <div className="flex gap-2">
                          {[0, 1, 2, 3].map(days => (
                            <button
                              key={days}
                              onClick={() => setSettings({ ...settings, reminderDaysBefore: days })}
                              className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${
                                settings.reminderDaysBefore === days
                                  ? 'bg-amber-500 text-white'
                                  : 'bg-gray-100 dark:bg-stone-700 text-gray-600 dark:text-stone-300'
                              }`}
                            >
                              {days === 0 ? 'उसी दिन' : `${days} दिन`}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Time Selection */}
                      <div>
                        <label className="text-xs font-bold text-gray-600 dark:text-stone-400 mb-2 block">
                          किस समय?
                        </label>
                        <input
                          type="time"
                          value={settings.reminderTime}
                          onChange={(e) => setSettings({ ...settings, reminderTime: e.target.value })}
                          className="w-full p-2 rounded-lg border border-gray-200 dark:border-stone-600 bg-white dark:bg-stone-700 text-gray-800 dark:text-stone-200 text-sm"
                        />
                      </div>
                    </div>
                  )}

                  {/* Morning Of Time Selection */}
                  {settings.reminderType === 'morning_of' && (
                    <div className="mt-4 pt-3 border-t border-gray-200 dark:border-stone-600">
                      <label className="text-xs font-bold text-gray-600 dark:text-stone-400 mb-2 block">
                        सुबह का समय चुनें
                      </label>
                      <div className="flex gap-2">
                        {['05:00', '06:00', '07:00', '08:00'].map(time => (
                          <button
                            key={time}
                            onClick={() => setSettings({ ...settings, reminderTime: time })}
                            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${
                              settings.reminderTime === time
                                ? 'bg-amber-500 text-white'
                                : 'bg-gray-100 dark:bg-stone-700 text-gray-600 dark:text-stone-300'
                            }`}
                          >
                            {time.replace(':00', ' AM')}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={handleSaveSettings}
                className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-bold text-sm hover:from-amber-600 hover:to-orange-600 transition-all flex items-center justify-center gap-2"
                data-testid="save-ekadashi-settings"
              >
                <Check size={18} /> सेटिंग्स सहेजें
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Compact Ekadashi Banner for Dashboard
export const EkadashiBanner: React.FC<{ onClick?: () => void }> = ({ onClick }) => {
  const [nextEkadashi, setNextEkadashi] = useState<EkadashiDate | null>(null);
  const [daysUntil, setDaysUntil] = useState(0);

  useEffect(() => {
    const upcoming = getUpcomingEkadashis(1);
    if (upcoming.length > 0) {
      setNextEkadashi(upcoming[0]);
      setDaysUntil(getDaysUntilEkadashi(upcoming[0]));
    }
  }, []);

  if (!nextEkadashi) return null;

  const isUrgent = daysUntil <= 1;

  return (
    <button
      onClick={onClick}
      className={`w-full p-3 rounded-xl border flex items-center justify-between gap-3 transition-all hover:shadow-md ${
        isUrgent 
          ? 'bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 border-amber-300 dark:border-amber-700' 
          : 'bg-white dark:bg-stone-800 border-gray-100 dark:border-stone-700'
      }`}
      data-testid="ekadashi-banner"
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${isUrgent ? 'bg-amber-500 text-white' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'}`}>
          {nextEkadashi.paksha === 'Shukla' ? <Sun size={18} /> : <Moon size={18} />}
        </div>
        <div className="text-left">
          <div className="font-bold text-sm text-gray-800 dark:text-stone-200 line-clamp-1">
            {nextEkadashi.name}
          </div>
          <div className="text-xs text-gray-500 dark:text-stone-400">
            {daysUntil === 0 ? 'आज है!' : daysUntil === 1 ? 'कल है!' : `${daysUntil} दिन बाकी`}
          </div>
        </div>
      </div>
      <ChevronRight size={18} className={isUrgent ? 'text-amber-600' : 'text-gray-400 dark:text-stone-500'} />
    </button>
  );
};
